import { authMetadata } from './metadata';
import {
    accountClient,
    authClient,
    checkoutsClient,
    customersClient,
    dashboardClient,
    filamentsClient,
    salesChannelsClient,
    paymentFeesClient,
    jobsClient,
    ordersClient,
    printerMaintenancesClient,
    printersClient,
    productsClient,
    transactionsClient,
} from './clients';
import { axiosResponse, grpcCall } from './errors';
import { dashboardDataFromProto } from './dashboardMapper';
import {
    accountResponseFromProto,
    checkoutFromProto,
    checkoutListItemFromProto,
    customerFromProto,
    customerListItemFromProto,
    deleteResponseFromProto,
    filamentFromProto,
    filamentListItemFromProto,
    salesChannelFromProto,
    paymentFeeFromProto,
    jobFromProto,
    loginResponseFromProto,
    registerResponseFromProto,
    nestedListResponse,
    orderFromProto,
    orderListItemFromProto,
    orderSummaryFromProto,
    paginatedListResponse,
    printerFromProto,
    printerListItemFromProto,
    printerMaintenanceFromProto,
    printerStatsResponseFromProto,
    productFromProto,
    productListItemFromProto,
    productSummaryFromProto,
    reconciliationFromProto,
    successResponseFromProto,
    transactionFromProto,
    updateAvatarResponseFromProto,
    updatePasswordResponseFromProto,
    updateProfileResponseFromProto,
    userFromProto,
} from './restMappers';
import {
    buildCreateCheckoutRequest,
    buildCreateCustomerRequest,
    buildCreateFilamentRequest,
    buildCreateSalesChannelRequest,
    buildCreatePaymentFeeRequest,
    buildCreateJobRequest,
    buildCreateMaintenanceRequest,
    buildCreatePrinterRequest,
    buildCreateProductRequest,
    buildCreateTransactionRequest,
    buildAdjustProductStockRequest,
    buildListJobsRequest,
    buildListOrdersRequest,
    buildListTransactionsRequest,
    buildOrdersSummaryRequest,
    buildListMaintenancesRequest,
    buildLoginRequest,
    buildRegisterRequest,
    buildPaginationRequest,
    buildProductsSummaryRequest,
    buildPrinterStatsRequest,
    buildUpdateAvatarRequest,
    buildUpdateCheckoutRequest,
    buildUpdateCustomerRequest,
    buildUpdateFilamentRequest,
    buildUpdateSalesChannelRequest,
    buildUpdatePaymentFeeRequest,
    buildUpdateJobRequest,
    buildUpdateMaintenanceRequest,
    buildUpdatePasswordRequest,
    buildUpdatePrinterRequest,
    buildUpdateProductRequest,
    buildUpdateProfileRequest,
    buildUpdateTransactionRequest,
} from './requestBuilders';
import { parseRequestBody } from './requestBody';
import { dateStringToTimestamp } from './timestamp';

const SKIP_PATH_PREFIXES = ['users/', 'quick-checkout', 'checkouts/batch'];
const SKIP_PATH_INCLUDES = ['pay-order'];

function parseUrl(url, params = {}) {
    let raw = String(url || '').trim();
    // Axios pode enviar URL absoluta após merge com baseURL
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
        try {
            const parsed = new URL(raw);
            raw = parsed.pathname + parsed.search;
        } catch {
            // mantém raw
        }
    }
    raw = raw.replace(/^\/api\/?/i, '').replace(/^\//, '');
    const [pathPart, queryPart] = raw.split('?');
    const path = pathPart.replace(/\/$/, '') || '';
    const search = new URLSearchParams(queryPart || '');
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            search.set(key, String(value));
        }
    });
    const query = {};
    search.forEach((value, key) => {
        query[key] = value;
    });
    return {
        path,
        segments: path.split('/').filter(Boolean),
        query,
    };
}

function shouldSkipGrpc(config) {
    if (config?.data instanceof FormData) {
        return true;
    }
    const contentType = config?.headers?.['Content-Type'] || config?.headers?.['content-type'] || '';
    if (typeof contentType === 'string' && contentType.includes('multipart/form-data')) {
        return true;
    }
    const { path } = parseUrl(config?.url, config?.params);
    if (!path) {
        return true;
    }
    if (SKIP_PATH_PREFIXES.some((prefix) => path === prefix.replace(/\/$/, '') || path.startsWith(prefix))) {
        return true;
    }
    if (SKIP_PATH_INCLUDES.some((part) => path.includes(part))) {
        return true;
    }
    return false;
}

function meta() {
    return authMetadata();
}

function getDashboard(startDate, endDate) {
    const { GetDashboardRequest } = require('./proto/acompanha/dashboard/v1/dashboard_pb');
    const request = new GetDashboardRequest();
    const startTs = dateStringToTimestamp(startDate);
    const endTs = dateStringToTimestamp(endDate);
    if (startTs) {
        request.setStartDate(startTs);
    }
    if (endTs) {
        request.setEndDate(endTs);
    }
    return grpcCall(dashboardClient().getDashboard(request, meta())).then((res) => {
        const data = dashboardDataFromProto(res.getData());
        return axiosResponse({ data });
    });
}

async function routeGet(path, segments, query) {
    if (path === 'dashboard' || path === 'dashboard/') {
        return getDashboard(query.start_date, query.end_date);
    }

    if (path === 'auth/login' || path === 'auth/register') {
        return null;
    }

    if (path === 'me/account') {
        return grpcCall(accountClient().getAccount({}, meta())).then((res) => (
            axiosResponse(accountResponseFromProto(res))
        ));
    }

    if (path === 'jobs/printer-stats') {
        const req = buildPrinterStatsRequest(query);
        return grpcCall(jobsClient().printerStats(req, meta())).then((res) => (
            axiosResponse(printerStatsResponseFromProto(res))
        ));
    }

    if (segments[0] === 'jobs' && segments.length === 1) {
        const req = buildListJobsRequest(query);
        return grpcCall(jobsClient().listJobs(req, meta())).then((res) => (
            axiosResponse(nestedListResponse(
                'jobs',
                (res.getJobsList() || []).map(jobFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'jobs' && segments.length === 2) {
        const { GetJobRequest } = require('./proto/acompanha/jobs/v1/jobs_pb');
        const req = new GetJobRequest();
        req.setUuid(segments[1]);
        return grpcCall(jobsClient().getJob(req, meta())).then((res) => (
            axiosResponse({ success: true, data: jobFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'printers' && segments.length === 1) {
        const { ListPrintersRequest } = require('./proto/acompanha/printers/v1/printers_pb');
        const req = new ListPrintersRequest();
        req.setPagination(buildPaginationRequest(query));
        return grpcCall(printersClient().listPrinters(req, meta())).then((res) => (
            axiosResponse(paginatedListResponse(
                'printers',
                (res.getPrintersList() || []).map(printerListItemFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'printers' && segments.length === 2) {
        const { GetPrinterRequest } = require('./proto/acompanha/printers/v1/printers_pb');
        const req = new GetPrinterRequest();
        req.setUuid(segments[1]);
        return grpcCall(printersClient().getPrinter(req, meta())).then((res) => (
            axiosResponse({ data: printerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 1) {
        const { ListFilamentsRequest } = require('./proto/acompanha/filaments/v1/filaments_pb');
        const req = new ListFilamentsRequest();
        req.setPagination(buildPaginationRequest(query));
        return grpcCall(filamentsClient().listFilaments(req, meta())).then((res) => (
            axiosResponse(paginatedListResponse(
                'materials',
                (res.getMaterialsList() || []).map(filamentListItemFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 2) {
        const { GetFilamentRequest } = require('./proto/acompanha/filaments/v1/filaments_pb');
        const req = new GetFilamentRequest();
        req.setUuid(segments[1]);
        return grpcCall(filamentsClient().getFilament(req, meta())).then((res) => (
            axiosResponse({ data: filamentFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'sales-channels' && segments.length === 1) {
        const { ListSalesChannelsRequest } = require('./proto/acompanha/sales_channels/v1/sales_channels_pb');
        const req = new ListSalesChannelsRequest();
        return grpcCall(salesChannelsClient().listSalesChannels(req, meta())).then((res) => (
            axiosResponse({
                channels: (res.getChannelsList() || []).map(salesChannelFromProto).filter(Boolean),
            })
        ));
    }

    if (segments[0] === 'payment-fees' && segments.length === 1) {
        const { ListPaymentFeesRequest } = require('./proto/acompanha/payment_fees/v1/payment_fees_pb');
        const req = new ListPaymentFeesRequest();
        return grpcCall(paymentFeesClient().listPaymentFees(req, meta())).then((res) => (
            axiosResponse({
                fees: (res.getFeesList() || []).map(paymentFeeFromProto).filter(Boolean),
            })
        ));
    }

    if (segments[0] === 'products' && segments[1] === 'summary') {
        const req = buildProductsSummaryRequest(query);
        return grpcCall(productsClient().getProductsSummary(req, meta())).then((res) => (
            axiosResponse(productSummaryFromProto(res))
        ));
    }

    if (segments[0] === 'products' && segments.length === 1) {
        const { ListProductsRequest } = require('./proto/acompanha/products/v1/products_pb');
        const req = new ListProductsRequest();
        req.setPagination(buildPaginationRequest(query));
        req.setSortBy(String(query.sort_by || 'created_at'));
        req.setSortOrder(String(query.sort_order || 'desc'));
        return grpcCall(productsClient().listProducts(req, meta())).then((res) => (
            axiosResponse(paginatedListResponse(
                'products',
                (res.getProductsList() || []).map(productListItemFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'products' && segments.length === 2) {
        const { GetProductRequest } = require('./proto/acompanha/products/v1/products_pb');
        const req = new GetProductRequest();
        req.setUuid(segments[1]);
        return grpcCall(productsClient().getProduct(req, meta())).then((res) => (
            axiosResponse({ data: productFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'customers' && segments.length === 1) {
        const { ListCustomersRequest } = require('./proto/acompanha/customers/v1/customers_pb');
        const req = new ListCustomersRequest();
        req.setPagination(buildPaginationRequest(query));
        return grpcCall(customersClient().listCustomers(req, meta())).then((res) => (
            axiosResponse(paginatedListResponse(
                'customers',
                (res.getCustomersList() || []).map(customerListItemFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'customers' && segments.length === 2) {
        const { GetCustomerRequest } = require('./proto/acompanha/customers/v1/customers_pb');
        const req = new GetCustomerRequest();
        req.setUuid(segments[1]);
        return grpcCall(customersClient().getCustomer(req, meta())).then((res) => (
            axiosResponse({ data: customerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'checkouts' && segments.length === 1) {
        const { ListCheckoutsRequest } = require('./proto/acompanha/checkouts/v1/checkouts_pb');
        const req = new ListCheckoutsRequest();
        req.setPagination(buildPaginationRequest(query));
        return grpcCall(checkoutsClient().listCheckouts(req, meta())).then((res) => (
            axiosResponse(paginatedListResponse(
                'checkouts',
                (res.getCheckoutsList() || []).map(checkoutListItemFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'checkouts' && segments.length === 2) {
        const { GetCheckoutRequest } = require('./proto/acompanha/checkouts/v1/checkouts_pb');
        const req = new GetCheckoutRequest();
        req.setUuid(segments[1]);
        return grpcCall(checkoutsClient().getCheckout(req, meta())).then((res) => (
            axiosResponse({ data: checkoutFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'orders' && segments[1] === 'summary') {
        const req = buildOrdersSummaryRequest(query);
        return grpcCall(ordersClient().getOrdersSummary(req, meta())).then((res) => (
            axiosResponse(orderSummaryFromProto(res))
        ));
    }

    if (segments[0] === 'orders' && segments.length === 1) {
        const req = buildListOrdersRequest(query);
        return grpcCall(ordersClient().listOrders(req, meta())).then((res) => (
            axiosResponse(paginatedListResponse(
                'orders',
                (res.getOrdersList() || []).map(orderListItemFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'orders' && segments.length === 3 && segments[2] === 'transaction') {
        const { GetOrderTransactionRequest } = require('./proto/acompanha/orders/v1/orders_pb');
        const req = new GetOrderTransactionRequest();
        req.setOrderUuid(segments[1]);
        return grpcCall(ordersClient().getOrderTransaction(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: {
                    transaction: transactionFromProto(res.getTransaction()),
                },
            })
        ));
    }

    if (segments[0] === 'orders' && segments.length === 2) {
        const { GetOrderRequest } = require('./proto/acompanha/orders/v1/orders_pb');
        const req = new GetOrderRequest();
        req.setUuid(segments[1]);
        return grpcCall(ordersClient().getOrder(req, meta())).then((res) => (
            axiosResponse({ success: true, data: orderFromProto(res.getData()) })
        ));
    }

    if (path === 'transactions/reconciliation') {
        return grpcCall(transactionsClient().reconciliation({}, meta())).then((res) => (
            axiosResponse(reconciliationFromProto(res.getData()))
        ));
    }

    if (segments[0] === 'transactions' && segments.length === 1) {
        // Lookup por authorization_id (reimportação de extrato) ainda é REST.
        if (query.authorization_ids) {
            return null;
        }
        const req = buildListTransactionsRequest(query);
        return grpcCall(transactionsClient().listTransactions(req, meta())).then((res) => (
            axiosResponse(nestedListResponse(
                'transactions',
                (res.getTransactionsList() || []).map(transactionFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'transactions' && segments.length === 2) {
        const { GetTransactionRequest } = require('./proto/acompanha/transactions/v1/transactions_pb');
        const req = new GetTransactionRequest();
        req.setUuid(segments[1]);
        return grpcCall(transactionsClient().getTransaction(req, meta())).then((res) => (
            axiosResponse({
                success: true,
                data: {
                    transaction: transactionFromProto(res.getTransaction()),
                },
            })
        ));
    }

    if (segments[0] === 'printer-maintenances' && segments.length === 1) {
        const req = buildListMaintenancesRequest(query);
        return grpcCall(printerMaintenancesClient().listMaintenances(req, meta())).then((res) => (
            axiosResponse(nestedListResponse(
                'maintenances',
                (res.getMaintenancesList() || []).map(printerMaintenanceFromProto).filter(Boolean),
                res.getMeta(),
            ))
        ));
    }

    if (segments[0] === 'printer-maintenances' && segments.length === 2) {
        const { GetMaintenanceRequest } = require('./proto/acompanha/printer_maintenances/v1/printer_maintenances_pb');
        const req = new GetMaintenanceRequest();
        req.setUuid(segments[1]);
        return grpcCall(printerMaintenancesClient().getMaintenance(req, meta())).then((res) => (
            axiosResponse({ success: true, data: printerMaintenanceFromProto(res.getData()) })
        ));
    }

    return null;
}

async function routePost(path, segments, data) {
    if (path === 'auth/login') {
        const req = buildLoginRequest(data || {});
        return grpcCall(authClient().login(req, meta())).then((res) => (
            axiosResponse(loginResponseFromProto(res))
        ));
    }

    if (path === 'auth/register') {
        const req = buildRegisterRequest(data || {});
        return grpcCall(authClient().register(req, meta())).then((res) => (
            axiosResponse(registerResponseFromProto(res))
        ));
    }

    if (segments[0] === 'jobs' && segments.length === 1) {
        const req = buildCreateJobRequest(data || {});
        return grpcCall(jobsClient().createJob(req, meta())).then((res) => (
            axiosResponse({ success: true, data: jobFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'printers' && segments.length === 1) {
        const req = buildCreatePrinterRequest(data || {});
        return grpcCall(printersClient().createPrinter(req, meta())).then((res) => (
            axiosResponse({ data: printerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 1) {
        const req = buildCreateFilamentRequest(data || {});
        return grpcCall(filamentsClient().createFilament(req, meta())).then((res) => (
            axiosResponse({ data: filamentFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'sales-channels' && segments.length === 1) {
        const req = buildCreateSalesChannelRequest(data || {});
        return grpcCall(salesChannelsClient().createSalesChannel(req, meta())).then((res) => (
            axiosResponse({ data: salesChannelFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'payment-fees' && segments.length === 1) {
        const req = buildCreatePaymentFeeRequest(data || {});
        return grpcCall(paymentFeesClient().createPaymentFee(req, meta())).then((res) => (
            axiosResponse({ data: paymentFeeFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'products' && segments.length === 3 && segments[2] === 'stock') {
        const req = buildAdjustProductStockRequest(segments[1], data || {});
        return grpcCall(productsClient().adjustProductStock(req, meta())).then((res) => (
            axiosResponse({ data: productListItemFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'products' && segments.length === 1) {
        const req = buildCreateProductRequest(data || {});
        return grpcCall(productsClient().createProduct(req, meta())).then((res) => (
            axiosResponse({ data: productFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'customers' && segments.length === 1) {
        const req = buildCreateCustomerRequest(data || {});
        return grpcCall(customersClient().createCustomer(req, meta())).then((res) => (
            axiosResponse({ data: customerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'checkouts' && segments.length === 1) {
        const req = buildCreateCheckoutRequest(data || {});
        return grpcCall(checkoutsClient().createCheckout(req, meta())).then((res) => (
            axiosResponse({ success: true, data: checkoutFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'transactions' && segments.length === 1) {
        const req = buildCreateTransactionRequest(data || {});
        return grpcCall(transactionsClient().createTransaction(req, meta())).then((res) => (
            axiosResponse({ success: Boolean(res.getSuccess()) })
        ));
    }

    if (segments[0] === 'printer-maintenances' && segments.length === 1) {
        const req = buildCreateMaintenanceRequest(data || {});
        return grpcCall(printerMaintenancesClient().createMaintenance(req, meta())).then((res) => (
            axiosResponse({ success: true, data: printerMaintenanceFromProto(res.getData()) })
        ));
    }

    return null;
}

async function routePut(path, segments, data) {
    if (path === 'me/avatar') {
        const req = buildUpdateAvatarRequest(data || {});
        return grpcCall(accountClient().updateAvatar(req, meta())).then((res) => (
            axiosResponse(updateAvatarResponseFromProto(res))
        ));
    }

    if (segments[0] === 'jobs' && segments.length === 2) {
        const req = buildUpdateJobRequest(segments[1], data || {});
        return grpcCall(jobsClient().updateJob(req, meta())).then((res) => (
            axiosResponse({ success: true, data: jobFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'printers' && segments.length === 2) {
        const req = buildUpdatePrinterRequest(segments[1], data || {});
        return grpcCall(printersClient().updatePrinter(req, meta())).then((res) => (
            axiosResponse({ data: printerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 2) {
        const req = buildUpdateFilamentRequest(segments[1], data || {});
        return grpcCall(filamentsClient().updateFilament(req, meta())).then((res) => (
            axiosResponse({ data: filamentFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'sales-channels' && segments.length === 2) {
        const req = buildUpdateSalesChannelRequest(segments[1], data || {});
        return grpcCall(salesChannelsClient().updateSalesChannel(req, meta())).then((res) => (
            axiosResponse({ data: salesChannelFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'payment-fees' && segments.length === 2) {
        const req = buildUpdatePaymentFeeRequest(segments[1], data || {});
        return grpcCall(paymentFeesClient().updatePaymentFee(req, meta())).then((res) => (
            axiosResponse({ data: paymentFeeFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'products' && segments.length === 2) {
        const req = buildUpdateProductRequest(segments[1], data || {});
        return grpcCall(productsClient().updateProduct(req, meta())).then((res) => (
            axiosResponse({ data: productFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'customers' && segments.length === 2) {
        const req = buildUpdateCustomerRequest(segments[1], data || {});
        return grpcCall(customersClient().updateCustomer(req, meta())).then((res) => (
            axiosResponse({ data: customerFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'checkouts' && segments.length === 2) {
        const req = buildUpdateCheckoutRequest(segments[1], data || {});
        return grpcCall(checkoutsClient().updateCheckout(req, meta())).then((res) => (
            axiosResponse({ success: true, data: checkoutFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'printer-maintenances' && segments.length === 2) {
        const req = buildUpdateMaintenanceRequest(segments[1], data || {});
        return grpcCall(printerMaintenancesClient().updateMaintenance(req, meta())).then((res) => (
            axiosResponse({ success: true, data: printerMaintenanceFromProto(res.getData()) })
        ));
    }

    if (segments[0] === 'transactions' && segments.length === 2) {
        const req = buildUpdateTransactionRequest(segments[1], data || {});
        return grpcCall(transactionsClient().updateTransaction(req, meta())).then((res) => (
            axiosResponse({ success: Boolean(res.getSuccess()) })
        ));
    }

    return null;
}

async function routePatch(path, segments, data) {
    if (path === 'me') {
        const req = buildUpdateProfileRequest(data || {});
        return grpcCall(accountClient().updateProfile(req, meta())).then((res) => (
            axiosResponse(updateProfileResponseFromProto(res))
        ));
    }

    if (path === 'me/password') {
        const req = buildUpdatePasswordRequest(data || {});
        return grpcCall(accountClient().updatePassword(req, meta())).then((res) => (
            axiosResponse(updatePasswordResponseFromProto(res))
        ));
    }

    return null;
}

async function routeDelete(path, segments) {
    if (segments[0] === 'jobs' && segments.length === 2) {
        const { DeleteJobRequest } = require('./proto/acompanha/jobs/v1/jobs_pb');
        const req = new DeleteJobRequest();
        req.setUuid(segments[1]);
        return grpcCall(jobsClient().deleteJob(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res, 'Job deleted successfully.'))
        ));
    }

    if (segments[0] === 'printers' && segments.length === 2) {
        const { DeletePrinterRequest } = require('./proto/acompanha/printers/v1/printers_pb');
        const req = new DeletePrinterRequest();
        req.setUuid(segments[1]);
        return grpcCall(printersClient().deletePrinter(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'filaments' && segments.length === 2) {
        const { DeleteFilamentRequest } = require('./proto/acompanha/filaments/v1/filaments_pb');
        const req = new DeleteFilamentRequest();
        req.setUuid(segments[1]);
        return grpcCall(filamentsClient().deleteFilament(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'sales-channels' && segments.length === 2) {
        const { DeleteSalesChannelRequest } = require('./proto/acompanha/sales_channels/v1/sales_channels_pb');
        const req = new DeleteSalesChannelRequest();
        req.setUuid(segments[1]);
        return grpcCall(salesChannelsClient().deleteSalesChannel(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'payment-fees' && segments.length === 2) {
        const { DeletePaymentFeeRequest } = require('./proto/acompanha/payment_fees/v1/payment_fees_pb');
        const req = new DeletePaymentFeeRequest();
        req.setUuid(segments[1]);
        return grpcCall(paymentFeesClient().deletePaymentFee(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'products' && segments.length === 2) {
        const { DeleteProductRequest } = require('./proto/acompanha/products/v1/products_pb');
        const req = new DeleteProductRequest();
        req.setUuid(segments[1]);
        return grpcCall(productsClient().deleteProduct(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'customers' && segments.length === 2) {
        const { DeleteCustomerRequest } = require('./proto/acompanha/customers/v1/customers_pb');
        const req = new DeleteCustomerRequest();
        req.setUuid(segments[1]);
        return grpcCall(customersClient().deleteCustomer(req, meta())).then((res) => (
            axiosResponse(deleteResponseFromProto(res))
        ));
    }

    if (segments[0] === 'checkouts' && segments.length === 2) {
        const { DeleteCheckoutRequest } = require('./proto/acompanha/checkouts/v1/checkouts_pb');
        const req = new DeleteCheckoutRequest();
        req.setUuid(segments[1]);
        return grpcCall(checkoutsClient().deleteCheckout(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res))
        ));
    }

    if (segments[0] === 'orders' && segments.length === 2) {
        const { DeleteOrderRequest } = require('./proto/acompanha/orders/v1/orders_pb');
        const req = new DeleteOrderRequest();
        req.setUuid(segments[1]);
        return grpcCall(ordersClient().deleteOrder(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res))
        ));
    }

    if (segments[0] === 'transactions' && segments.length === 2) {
        const { DeleteTransactionRequest } = require('./proto/acompanha/transactions/v1/transactions_pb');
        const req = new DeleteTransactionRequest();
        req.setUuid(segments[1]);
        return grpcCall(transactionsClient().deleteTransaction(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res))
        ));
    }

    if (segments[0] === 'printer-maintenances' && segments.length === 2) {
        const { DeleteMaintenanceRequest } = require('./proto/acompanha/printer_maintenances/v1/printer_maintenances_pb');
        const req = new DeleteMaintenanceRequest();
        req.setUuid(segments[1]);
        return grpcCall(printerMaintenancesClient().deleteMaintenance(req, meta())).then((res) => (
            axiosResponse(successResponseFromProto(res))
        ));
    }

    return null;
}

/**
 * Axios-like transport router.
 * @param {{ method?: string, url?: string, params?: object, data?: any }} config
 * @returns {Promise<{ data: any, status: number, statusText: string, headers: object, config: object }>|null>}
 */
export async function grpcTransport(config) {
    if (shouldSkipGrpc(config)) {
        return null;
    }

    const method = String(config?.method || 'get').toLowerCase();
    const { path, segments, query } = parseUrl(config?.url, config?.params);

    let response = null;
    if (method === 'get') {
        response = await routeGet(path, segments, query);
    } else if (method === 'post') {
        response = await routePost(path, segments, parseRequestBody(config?.data));
    } else if (method === 'put') {
        response = await routePut(path, segments, parseRequestBody(config?.data));
    } else if (method === 'patch') {
        response = await routePatch(path, segments, parseRequestBody(config?.data));
    } else if (method === 'delete') {
        response = await routeDelete(path, segments);
    }

    return response ?? null;
}
